import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FETCH_CASCADING_OPTIONS, 
  CLEAR_CASCADING_OPTIONS, 
  RESET_CASCADING_STATE 
} from '../store/createList';

/**
 * Custom hook for managing cascading dropdown options
 * Provides functions to fetch cascading options and clear dependent options
 */
export const useCascadingOptions = () => {
  const dispatch = useDispatch();
  
  const cascadingOptions = useSelector(state => state.createlist?.cascadingOptions || {});
  const cascadingLoading = useSelector(state => state.createlist?.cascadingLoading || {});
  const error = useSelector(state => state.createlist?.error);

  /**
   * Fetch cascading options for a specific field type
   * @param {Object} params - Parameters for cascading options
   * @param {string} params.field_type - Type of field: 'thpc', 'disease_state', 'molecule', 'brand', 'indication'
   * @param {string} params.department_field - Department: 'RETAIL' or 'MEDICAL'
   * @param {string} params.list_type - List type: 'brnd&ind', 'thpc&dsst&mdpr', 'thpc&dsst'
   * @param {string} params.clone_type - Clone type: 'scratch' or 'existing'
   * @param {string} [params.thpc_nm] - Selected THPC name
   * @param {string} [params.dsst_nm] - Selected disease state name
   * @param {string} [params.brand_nm] - Selected brand name
   */
  const fetchCascadingOptions = useCallback((params) => {
    dispatch(FETCH_CASCADING_OPTIONS(params));
  }, [dispatch]);

  /**
   * Clear cascading options for dependent fields
   * @param {string[]} fieldTypes - Array of field types to clear
   */
  const clearCascadingOptions = useCallback((fieldTypes) => {
    dispatch(CLEAR_CASCADING_OPTIONS({ fieldTypes }));
  }, [dispatch]);

  /**
   * Reset all cascading state
   */
  const resetCascadingState = useCallback(() => {
    dispatch(RESET_CASCADING_STATE());
  }, [dispatch]);

  /**
   * Helper function to get formatted options for dropdown
   * @param {string} fieldType - Field type to get options for
   * @param {string} listType - List type to determine field mapping
   * @param {string} cloneType - Clone type to determine field mapping
   * @returns {Array} Formatted options array with label and value
   */
  const getFormattedOptions = useCallback((fieldType, listType, cloneType) => {
    const options = cascadingOptions[fieldType] || [];
    
    if (!options.length) return [];

    let fieldMapping = {};
    
    if (cloneType === 'scratch') {
      fieldMapping = {
        thpc: { label: 'thpc_nm', value: 'thpc_nm' },
        disease_state: { label: 'disease_nm', value: 'disease_nm' },
        molecule: { label: 'molecule_nm', value: 'molecule_nm' },
        brand: { label: 'brnd_nm', value: 'brnd_nm' },
        indication: { label: 'indctn_grp_nm', value: 'indctn_grp_nm' }
      };
    } else if (cloneType === 'existing') {
      fieldMapping = {
        thpc: { label: 'thpc_nm', value: 'thpc_nm' },
        disease_state: { label: 'dsst_nm', value: 'dsst_nm' },
        molecule: { label: 'mdpr_nm', value: 'mdpr_nm' },
        brand: { label: 'brand_nm', value: 'brand_nm' },
        indication: { label: 'indication_nm', value: 'indication_nm' }
      };
    }

    const mapping = fieldMapping[fieldType];
    if (!mapping) return [];

    return options.map(item => ({
      label: item[mapping.label] || '',
      value: item[mapping.value] || '',
      rawData: item // Include raw data for additional processing if needed
    })).filter(item => item.label && item.value);
  }, [cascadingOptions]);

  /**
   * Helper function to handle cascading when a dropdown selection changes
   * @param {string} fieldType - The field type that changed
   * @param {string} selectedValue - The new selected value
   * @param {Object} baseParams - Base parameters (department_field, list_type, clone_type)
   * @param {Object} currentSelections - Current selections object
   */
  const handleCascadingChange = useCallback((fieldType, selectedValue, baseParams, currentSelections) => {
    // Define the cascading hierarchy
    const hierarchy = ['thpc', 'disease_state', 'molecule', 'brand', 'indication'];
    const currentIndex = hierarchy.indexOf(fieldType);
    
    if (currentIndex === -1) return;

    // Clear all dependent fields
    const dependentFields = hierarchy.slice(currentIndex + 1);
    if (dependentFields.length > 0) {
      clearCascadingOptions(dependentFields);
    }

    // If a value was selected and there are dependent fields, fetch the next level
    if (selectedValue && currentIndex < hierarchy.length - 1) {
      const nextFieldType = hierarchy[currentIndex + 1];
      
      // Build parameters for the next level
      const params = {
        ...baseParams,
        field_type: nextFieldType
      };

      // Add cascading parameters based on current selections
      if (fieldType === 'thpc') {
        params.thpc_nm = selectedValue;
      } else if (fieldType === 'disease_state') {
        params.thpc_nm = currentSelections.thpc;
        params.dsst_nm = selectedValue;
      } else if (fieldType === 'molecule' || fieldType === 'brand') {
        params.thpc_nm = currentSelections.thpc;
        params.dsst_nm = currentSelections.disease_state;
        if (fieldType === 'brand') {
          params.brand_nm = selectedValue;
        }
      }

      fetchCascadingOptions(params);
    }
  }, [fetchCascadingOptions, clearCascadingOptions]);

  return {
    // State
    cascadingOptions,
    cascadingLoading,
    error,
    
    // Actions
    fetchCascadingOptions,
    clearCascadingOptions,
    resetCascadingState,
    
    // Helpers
    getFormattedOptions,
    handleCascadingChange
  };
};

export default useCascadingOptions;
